import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Badge, EmptyState } from '../components/ui.js';
import { Icon } from '../components/Icon.js';

interface Faq {
  faq_id: string; question: string; answer: string;
  priority: number; language_id: string; embedding_state: string;
}
interface Doc {
  document_id: string; filename: string; file_size: number;
  status: string; chunk_count: number;
}

const LANGS = [
  { id: 'ar-IQ', labelKey: 'page.knowledge.faq.lang.ar' },
  { id: 'en-US', labelKey: 'page.knowledge.faq.lang.en' },
];

export function Knowledge() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useLang();

  const { data: faqs } = useQuery({
    queryKey: ['faq'],
    queryFn: () => api.get<{ items: Faq[] }>('/knowledge/faq'),
  });
  const { data: docs } = useQuery({
    queryKey: ['documents'],
    queryFn: () => api.get<{ items: Doc[]; storage_bytes_used: number; storage_bytes_limit: number }>('/knowledge/documents'),
  });

  const [faqForm, setFaqForm] = useState({ question: '', answer: '', language_id: 'ar-IQ', priority: 0 });
  const createFaq = useMutation({
    mutationFn: () => api.post('/knowledge/faq', faqForm),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['faq'] });
      setFaqForm({ question: '', answer: '', language_id: 'ar-IQ', priority: 0 });
    },
  });
  const deleteFaq = useMutation({
    mutationFn: (faqId: string) => api.delete(`/knowledge/faq/${faqId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['faq'] }),
  });

  const usedMB = (docs?.storage_bytes_used ?? 0) / 1024 / 1024;
  const limitMB = (docs?.storage_bytes_limit ?? 0) / 1024 / 1024;
  const pct = limitMB > 0 ? Math.min(100, (usedMB / limitMB) * 100) : 0;

  return (
    <div className="wrap section-gap">
      <PageHead title={t('page.knowledge.title')} sub={t('page.knowledge.sub')} />

      {/* FAQ */}
      <div>
        <div className="between" style={{ marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>{t('page.knowledge.faq.h')}</h2>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>
              {faqs?.items.length ?? 0} {t('page.knowledge.faq.count')}
            </div>
          </div>
        </div>

        <div className="card card--pad" style={{ marginBottom: 18 }}>
          <div className="field">
            <label className="field__label">{t('page.knowledge.faq.q')}</label>
            <input
              className="input"
              value={faqForm.question}
              onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
              placeholder={t('page.knowledge.faq.qPh')}
            />
          </div>
          <div className="field">
            <label className="field__label">{t('page.knowledge.faq.a')}</label>
            <textarea
              className="textarea"
              rows={3}
              value={faqForm.answer}
              onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
              placeholder={t('page.knowledge.faq.aPh')}
            />
          </div>
          <div className="between" style={{ flexWrap: 'wrap', gap: 14 }}>
            <div className="flex aic gap12" style={{ flexWrap: 'wrap' }}>
              <div className="seg">
                {LANGS.map((l) => (
                  <button
                    key={l.id}
                    className={faqForm.language_id === l.id ? 'on' : ''}
                    onClick={() => setFaqForm({ ...faqForm, language_id: l.id })}
                    style={{ padding: '6px 12px', fontSize: 12.5 }}
                  >{t(l.labelKey)}</button>
                ))}
              </div>
              <label className="flex aic gap8" style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                <span>{t('page.knowledge.faq.priority')}</span>
                <input
                  type="number"
                  className="input"
                  style={{ width: 70, padding: '7px 10px', fontSize: 13, textAlign: 'center' }}
                  value={faqForm.priority}
                  onChange={(e) => setFaqForm({ ...faqForm, priority: Number(e.target.value) || 0 })}
                />
              </label>
            </div>
            <button
              className="btn btn--primary"
              disabled={!faqForm.question || !faqForm.answer || createFaq.isPending}
              onClick={() => createFaq.mutate()}
            >
              <Icon name="plus" />{createFaq.isPending ? t('page.knowledge.faq.adding') : t('page.knowledge.faq.add')}
            </button>
          </div>
        </div>

        {(faqs?.items ?? []).length === 0 ? (
          <EmptyState icon="book" h={t('page.knowledge.faq.empty.h')} p={t('page.knowledge.faq.empty.p')} />
        ) : (
          <div className="grid2">
            {(faqs?.items ?? [])
              .slice()
              .sort((a, b) => b.priority - a.priority)
              .map((f) => (
                <div className="card card--pad" key={f.faq_id}>
                  <div className="between" style={{ alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.55 }}>{f.question}</div>
                      <div className="muted" style={{ fontSize: 13.5, marginTop: 8, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{f.answer}</div>
                    </div>
                    <button
                      onClick={() => { if (confirm(t('page.knowledge.faq.delConfirm'))) deleteFaq.mutate(f.faq_id); }}
                      className="iconbtn"
                      style={{ flexShrink: 0, color: 'var(--ink-3)' }}
                      aria-label={t('common.delete')}
                    >
                      <Icon name="trash" size={16} />
                    </button>
                  </div>
                  <div className="flex aic gap8 wrapf" style={{ marginTop: 14 }}>
                    <Badge kind="muted" plain>
                      {f.language_id === 'ar-IQ' ? t('page.knowledge.faq.lang.ar') : f.language_id === 'en-US' ? t('page.knowledge.faq.lang.en') : f.language_id}
                    </Badge>
                    {f.priority > 0 && <Badge kind="accent" plain>{t('page.knowledge.priorityBadge')} {f.priority}</Badge>}
                    <EmbeddingBadge state={f.embedding_state} />
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div>
        <div className="between" style={{ marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>{t('page.knowledge.doc.h')}</h2>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{t('page.knowledge.doc.sub')}</div>
          </div>
        </div>

        <div className="card card--pad" style={{ marginBottom: 14 }}>
          <div className="between" style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{t('page.knowledge.doc.used')}</div>
            <div className="mono" style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {usedMB.toFixed(1)} / {limitMB.toFixed(0)} MB
            </div>
          </div>
          <div style={{ height: 8, background: 'var(--bg-4)', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pct}%`,
              background: pct > 90 ? 'var(--danger)' : pct > 70 ? 'var(--warn)' : 'var(--accent)',
              transition: 'width .25s ease',
            }} />
          </div>
        </div>

        {(docs?.items ?? []).length === 0 ? (
          <EmptyState icon="doc" h={t('page.knowledge.doc.empty.h')} p={t('page.knowledge.doc.empty.p')} />
        ) : (
          <div className="grid2">
            {(docs?.items ?? []).map((d) => (
              <div className="card card--pad" key={d.document_id}>
                <div className="flex aic gap12">
                  <div style={{
                    width: 44, height: 44, borderRadius: 11, background: 'var(--bg-3)',
                    display: 'grid', placeItems: 'center', color: 'var(--accent)', flexShrink: 0,
                  }}><Icon name="doc" size={20} /></div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.filename}</div>
                    <div className="muted mono" style={{ fontSize: 12, marginTop: 3 }}>
                      {(d.file_size / 1024).toFixed(1)} KB · {d.chunk_count}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <EmbeddingBadge state={d.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmbeddingBadge({ state }: { state: string }) {
  const { t } = useLang();
  if (state === 'embedded' || state === 'ready' || state === 'indexed') {
    return <Badge kind="ok" plain icon="checkcircle">{t('embed.embedded')}</Badge>;
  }
  if (state === 'failed' || state === 'error') {
    return <Badge kind="danger" plain>{t('embed.failed')}</Badge>;
  }
  return <Badge kind="warn" plain icon="refresh">{t('embed.pending')}</Badge>;
}
